// The backend stores a NotificationTitleKey and a payload, never a rendered string, so a notification
// is read in the reader's language rather than the writer's. Every sentence is a whole $localize
// string: German word order differs, so a sentence is never assembled from translated fragments.

export interface NotificationTextSource {
  titleKey: string;
  payloadJson?: string;
  actorName?: string;
  actorEmail?: string;
  occurredAt?: string;
}

export interface NotificationText {
  title: string;
  description: string;
}

type Payload = Record<string, any>;

interface FieldChange {
  field: string;
  value: string;
}

const MAX_QUOTE_LENGTH = 140;
const MAX_SPARK_EXCERPT_LENGTH = 60;

const EMPTY_VALUE = '–';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T.*)?$/;
const DAY_MS = 86400000;

// Money and dates stay German in both languages, as everywhere else in the app.
const DATE_LOCALE = 'de-DE';

const TITLES: Record<string, (actor: string) => string> = {
  ProjectChanged: actor => $localize`:@@notificationTitleProjectChanged:${actor}:actor: edited a project`,
  ProjectTeamChanged: actor =>
    $localize`:@@notificationTitleProjectTeamChanged:${actor}:actor: changed the project team`,
  RoleAssigned: actor => $localize`:@@notificationTitleRoleAssigned:${actor}:actor: added you to the project team`,
  RoleChanged: actor => $localize`:@@notificationTitleRoleChanged:${actor}:actor: changed your role`,
  RoleRemoved: actor => $localize`:@@notificationTitleRoleRemoved:${actor}:actor: removed you from the project team`,
  TaskAssigned: actor => $localize`:@@notificationTitleTaskAssigned:${actor}:actor: assigned a task to you`,
  TaskUnassigned: actor => $localize`:@@notificationTitleTaskUnassigned:${actor}:actor: unassigned you from a task`,
  TaskCompleted: actor => $localize`:@@notificationTitleTaskCompleted:${actor}:actor: completed a task`,
  TaskNotRequired: actor =>
    $localize`:@@notificationTitleTaskNotRequired:${actor}:actor: marked a task as no longer needed`,
  TaskUnderReview: actor => $localize`:@@notificationTitleTaskUnderReview:${actor}:actor: asked you for a review`,
  TaskRejected: actor => $localize`:@@notificationTitleTaskRejected:${actor}:actor: rejected a task`,
  TaskMentioned: actor => $localize`:@@notificationTitleTaskMentioned:${actor}:actor: mentioned you`,
  TaskCommented: actor => $localize`:@@notificationTitleTaskCommented:${actor}:actor: commented`,
  TaskReminder: () => $localize`:@@notificationTitleTaskReminder:Reminder for a task`,
  SparkMentioned: actor => $localize`:@@notificationTitleSparkMentioned:${actor}:actor: mentioned you in a Spark`,
  SparkAnswered: actor => $localize`:@@notificationTitleSparkAnswered:${actor}:actor: replied to your Spark`,
  SparkReacted: actor => $localize`:@@notificationTitleSparkReacted:${actor}:actor: reacted to your Spark`,
  SparkLiked: actor => $localize`:@@notificationTitleSparkLiked:${actor}:actor: liked your Spark`,
  ContractChanged: actor => $localize`:@@notificationTitleContractChanged:${actor}:actor: edited a contract`,
  DeadlineAssigned: actor => $localize`:@@notificationTitleDeadlineAssigned:${actor}:actor: assigned a deadline to you`,
  CostChanged: actor => $localize`:@@notificationTitleCostChanged:${actor}:actor: adjusted costs`,
  CostDetailsChanged: actor => $localize`:@@notificationTitleCostDetailsChanged:${actor}:actor: edited a cost item`,
  WeeklySummary: () => $localize`:@@notificationTitleWeeklySummary:Your weekly report is ready`,
  MonthlySummary: () => $localize`:@@notificationTitleMonthlySummary:Your monthly report is ready`,
  CostOverviewReady: () => $localize`:@@notificationTitleCostOverviewReady:Your cost overview is ready`,
  DatabaseExportReady: () => $localize`:@@notificationTitleDatabaseExportReady:Your database export is ready`,
  DatabaseExportFailed: () => $localize`:@@notificationTitleDatabaseExportFailed:The database export didn’t work`,
};

// Status and priority travel as the API's own tokens and read as words inside a sentence, so they
// are lower case here rather than the capitalised labels the rest of the app shows.
const STATUS_WORDS: Record<string, string> = {
  OPEN: $localize`:@@notificationStatusOpen:open`,
  IN_PROGRESS: $localize`:@@notificationStatusInProgress:in progress`,
  IN_REVIEW: $localize`:@@notificationStatusInReview:in review`,
  DONE: $localize`:@@notificationStatusDone:done`,
  NOT_REQUIRED: $localize`:@@notificationStatusNotRequired:not required`,
  REJECTED: $localize`:@@notificationStatusRejected:rejected`,
};

const PRIORITY_WORDS: Record<string, string> = {
  LOW: $localize`:@@notificationPriorityLow:low`,
  MEDIUM: $localize`:@@notificationPriorityMedium:medium`,
  HIGH: $localize`:@@notificationPriorityHigh:high`,
  CRITICAL: $localize`:@@notificationPriorityCritical:critical`,
};

// A field named inside a sentence carries its article ("the partner", „der Partner“). The order is
// the order a change is picked to lead the description; the rest are counted.
const PROJECT_FIELDS: readonly { field: string; label: string }[] = [
  { field: 'Status', label: $localize`:@@notificationFieldProjectStatus:the status` },
  { field: 'Partner', label: $localize`:@@notificationFieldProjectPartner:the partner` },
  { field: 'Epc', label: $localize`:@@notificationFieldProjectEpc:the EPC` },
  { field: 'AreaType', label: $localize`:@@notificationFieldProjectAreaType:the area type` },
  { field: 'Nr', label: $localize`:@@notificationFieldProjectNr:the project number` },
  { field: 'ProjectName', label: $localize`:@@notificationFieldProjectName:the project name` },
];

const CONTRACT_FIELDS: readonly { field: string; label: string }[] = [
  { field: 'Status', label: $localize`:@@notificationFieldContractStatus:the status` },
  { field: 'PartyExtern', label: $localize`:@@notificationFieldContractPartyExtern:the external contract party` },
  { field: 'PartyIntern', label: $localize`:@@notificationFieldContractPartyIntern:the internal contract party` },
  { field: 'StartDate', label: $localize`:@@notificationFieldContractStartDate:the contract start` },
  { field: 'EndDate', label: $localize`:@@notificationFieldContractEndDate:the contract end` },
  { field: 'ContractType', label: $localize`:@@notificationFieldContractType:the contract type` },
  { field: 'ContractName', label: $localize`:@@notificationFieldContractName:the name` },
  { field: 'FrameworkContract', label: $localize`:@@notificationFieldContractFramework:the framework contract` },
  { field: 'Note', label: $localize`:@@notificationFieldContractNote:the note` },
];

const COST_FIELDS: readonly { field: string; label: string }[] = [
  { field: 'ServiceProvider', label: $localize`:@@notificationFieldCostServiceProvider:the service provider` },
  { field: 'Debitor', label: $localize`:@@notificationFieldCostDebitor:the debtor` },
  { field: 'PaidGross', label: $localize`:@@notificationFieldCostPaidGross:the paid amount (gross)` },
];

// Report sections carry a German heading for the mail; the bell names them by key, with a count.
const REPORT_SECTIONS: Record<string, (count: number) => string> = {
  UpcomingMilestones: count =>
    count === 1
      ? $localize`:@@notificationReportMilestonesOne:1 milestone`
      : $localize`:@@notificationReportMilestones:${count}:count: milestones`,
  TopTasks: count =>
    count === 1
      ? $localize`:@@notificationReportTasksOne:1 priority task`
      : $localize`:@@notificationReportTasks:${count}:count: priority tasks`,
  OverdueCosts: count =>
    count === 1
      ? $localize`:@@notificationReportOverdueCostsOne:1 overdue cost item`
      : $localize`:@@notificationReportOverdueCosts:${count}:count: overdue cost items`,
  PostponedMilestones: count =>
    count === 1
      ? $localize`:@@notificationReportPostponedMilestonesOne:1 postponed milestone`
      : $localize`:@@notificationReportPostponedMilestones:${count}:count: postponed milestones`,
  MissedMilestones: count =>
    count === 1
      ? $localize`:@@notificationReportMissedMilestonesOne:1 missed milestone`
      : $localize`:@@notificationReportMissedMilestones:${count}:count: missed milestones`,
  PhaseChanges: count =>
    count === 1
      ? $localize`:@@notificationReportPhaseChangesOne:1 project with a phase change`
      : $localize`:@@notificationReportPhaseChanges:${count}:count: projects with a phase change`,
  NewProjects: count =>
    count === 1
      ? $localize`:@@notificationReportNewProjectsOne:1 new project`
      : $localize`:@@notificationReportNewProjects:${count}:count: new projects`,
  CompletedMilestones: count =>
    count === 1
      ? $localize`:@@notificationReportCompletedMilestonesOne:1 reached milestone`
      : $localize`:@@notificationReportCompletedMilestones:${count}:count: reached milestones`,
  PlanCostChanges: count =>
    count === 1
      ? $localize`:@@notificationReportPlanCostChangesOne:1 plan cost change`
      : $localize`:@@notificationReportPlanCostChanges:${count}:count: plan cost changes`,
};

function parse(payloadJson?: string): Payload {
  if (!payloadJson) return {};

  try {
    const payload = JSON.parse(payloadJson);
    return payload && typeof payload === 'object' ? payload : {};
  } catch {
    // A payload that cannot be read costs the row its description, not its delivery.
    return {};
  }
}

function text(value: any): string {
  if (value === null || value === undefined || typeof value === 'object') return '';

  const result = String(value).trim();

  return result === EMPTY_VALUE ? '' : result;
}

function quoted(value: string): string {
  return $localize`:@@notificationQuoted:“${value}:text:”`;
}

function truncate(value: string, length = MAX_QUOTE_LENGTH): string {
  return value.length > length ? `${value.slice(0, length - 1).trimEnd()} …` : value;
}

function toDate(value: any): Date | null {
  const raw = text(value);

  if (!ISO_DATE.test(raw)) return null;

  // A bare yyyy-MM-dd is a calendar day; parsing it as UTC would shift it a day west of Greenwich.
  const date = raw.length === 10 ? new Date(`${raw}T00:00:00`) : new Date(raw);

  return isNaN(date.getTime()) ? null : date;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(DATE_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// A range's start drops its year ("08.10. to 22.10.2026") unless the range crosses a year.
function formatRangeStart(start: Date, end: Date): string {
  if (start.getFullYear() !== end.getFullYear()) return formatDate(start);

  return start.toLocaleDateString(DATE_LOCALE, { day: '2-digit', month: '2-digit' });
}

function displayValue(value: any): string {
  if (typeof value === 'boolean') {
    return value ? $localize`:@@notificationValueYes:Yes` : $localize`:@@notificationValueNo:No`;
  }

  const date = toDate(value);

  return date ? formatDate(date) : text(value);
}

function moreChanges(count: number): string {
  if (count <= 0) return '';

  return count === 1
    ? ' ' + $localize`:@@notificationMoreChangesOne:Plus 1 more change.`
    : ' ' + $localize`:@@notificationMoreChanges:Plus ${count}:count: more changes.`;
}

// Whatever the backend nulled out did not move, so a present field, or its Previous sibling, is what
// "changed" means.
function changedFields(payload: Payload, fields: readonly { field: string; label: string }[]): FieldChange[] {
  return fields.flatMap(({ field, label }) => {
    const hasCurrent = payload[field] !== null && payload[field] !== undefined;
    const hasPrevious = payload[`Previous${field}`] !== null && payload[`Previous${field}`] !== undefined;

    if (!hasPrevious && !(hasCurrent && field !== 'ProjectName')) return [];

    return [{ field: label, value: displayValue(payload[field]) }];
  });
}

function actorOf(source: NotificationTextSource): string {
  return (source.actorName || source.actorEmail || '').trim() || $localize`:@@notificationActorSomeone:Someone`;
}

function title(source: NotificationTextSource, payload: Payload): string {
  const actor = actorOf(source);

  switch (source.titleKey) {
    case 'ProjectDatesChanged':
      return scheduleTitle(actor, payload);
    case 'TaskChanged':
      return taskChangedTitle(actor, payload);
    case 'DeadlineApproaching':
      return deadlineTitle(payload, source.occurredAt);
    case 'CostThresholdExceeded':
      return costThresholdTitle(payload);
  }

  return TITLES[source.titleKey]?.(actor) ?? source.titleKey;
}

// #1
function projectChanged(payload: Payload): string {
  const project = text(payload.ProjectName);
  const changes = changedFields(payload, PROJECT_FIELDS);
  const teamChanges = Array.isArray(payload.TeamChangeList) ? payload.TeamChangeList.length : 0;

  if (!changes.length) {
    return teamChanges
      ? teamChanged(payload)
      : $localize`:@@notificationBodyProjectEdited:${project}:project: was edited.`;
  }

  const [first] = changes;
  const more = moreChanges(changes.length - 1 + teamChanges);

  if (!first.value) {
    return (
      $localize`:@@notificationBodyProjectFieldCleared:In ${project}:project:, ${first.field}:field: was removed.` +
      more
    );
  }

  return (
    $localize`:@@notificationBodyProjectField:In ${project}:project:, ${first.field}:field: is now ${first.value}:value:.` +
    more
  );
}

interface ScheduleChange {
  ItemType?: string;
  Name?: string;
  PreviousStart?: string;
  PreviousEnd?: string;
  Start?: string;
  End?: string;
  Moved?: boolean;
}

function scheduleChanges(payload: Payload): ScheduleChange[] {
  return Array.isArray(payload.ScheduleChanges) ? payload.ScheduleChanges : [];
}

// The title follows the item the user moved; anything that moved with it is only counted.
function movedScheduleItem(payload: Payload): ScheduleChange | undefined {
  const changes = scheduleChanges(payload);

  return changes.find(change => change.Moved) ?? changes[0];
}

// #2a–2c
function scheduleTitle(actor: string, payload: Payload): string {
  switch (movedScheduleItem(payload)?.ItemType) {
    case 'Milestone':
      return $localize`:@@notificationTitleMilestoneMoved:${actor}:actor: moved a milestone`;
    case 'Package':
      return $localize`:@@notificationTitleWorkPackageMoved:${actor}:actor: moved a work package`;
    case 'Phase':
      return $localize`:@@notificationTitlePhaseMoved:${actor}:actor: moved a phase`;
    default:
      return $localize`:@@notificationTitleProjectDatesChanged:${actor}:actor: changed the schedule`;
  }
}

// #2a–2d
function scheduleMoved(payload: Payload): string {
  const project = text(payload.ProjectName);
  const moved = movedScheduleItem(payload);
  const start = toDate(moved?.Start);
  const end = toDate(moved?.End);

  if (!moved || !start || !end) {
    const details = text(payload.ChangedItems);
    return details
      ? $localize`:@@notificationBodyScheduleLegacy:In ${project}:project:: ${details}:details:`
      : $localize`:@@notificationBodyScheduleEdited:The schedule of ${project}:project: was changed.`;
  }

  const item = text(moved.Name);
  const dependents = scheduleChanges(payload).length - 1;
  const previousStart = toDate(moved.PreviousStart);
  const previousEnd = toDate(moved.PreviousEnd);

  const date = formatDate(end);
  const from = formatRangeStart(start, end);
  const to = formatDate(end);

  if (dependents > 0) {
    const along =
      ' ' +
      (dependents === 1
        ? $localize`:@@notificationBodyScheduleDependentsOne:1 dependent item was moved along with it.`
        : $localize`:@@notificationBodyScheduleDependents:${dependents}:count: dependent items were moved along with it.`);

    switch (moved.ItemType) {
      case 'Milestone':
        return (
          $localize`:@@notificationBodyMilestoneMovedWithDependents:In ${project}:project:, the milestone “${item}:item:” is now on ${date}:date:.` +
          along
        );
      case 'Package':
        return (
          $localize`:@@notificationBodyWorkPackageMovedWithDependents:In ${project}:project:, the work package “${item}:item:” now runs from ${from}:start: to ${to}:end:.` +
          along
        );
      default:
        return (
          $localize`:@@notificationBodyPhaseMovedWithDependents:In ${project}:project:, the phase “${item}:item:” now runs from ${from}:start: to ${to}:end:.` +
          along
        );
    }
  }

  if (moved.ItemType === 'Milestone') {
    return previousEnd
      ? $localize`:@@notificationBodyMilestoneMoved:In ${project}:project:, the milestone “${item}:item:” is now on ${date}:date: instead of ${formatDate(previousEnd)}:oldDate:.`
      : $localize`:@@notificationBodyMilestoneMovedWithDependents:In ${project}:project:, the milestone “${item}:item:” is now on ${date}:date:.`;
  }

  if (!previousStart || !previousEnd) {
    return moved.ItemType === 'Package'
      ? $localize`:@@notificationBodyWorkPackageMovedWithDependents:In ${project}:project:, the work package “${item}:item:” now runs from ${from}:start: to ${to}:end:.`
      : $localize`:@@notificationBodyPhaseMovedWithDependents:In ${project}:project:, the phase “${item}:item:” now runs from ${from}:start: to ${to}:end:.`;
  }

  const oldFrom = formatRangeStart(previousStart, previousEnd);
  const oldTo = formatDate(previousEnd);

  return moved.ItemType === 'Package'
    ? $localize`:@@notificationBodyWorkPackageMoved:In ${project}:project:, the work package “${item}:item:” now runs from ${from}:start: to ${to}:end: instead of ${oldFrom}:oldStart: to ${oldTo}:oldEnd:.`
    : $localize`:@@notificationBodyPhaseMoved:In ${project}:project:, the phase “${item}:item:” now runs from ${from}:start: to ${to}:end: instead of ${oldFrom}:oldStart: to ${oldTo}:oldEnd:.`;
}

interface TeamChange {
  Role?: string;
  Previous?: string;
  Current?: string;
}

// #3
function teamChanged(payload: Payload): string {
  const project = text(payload.ProjectName);
  const changes: TeamChange[] = Array.isArray(payload.TeamChangeList) ? payload.TeamChangeList : [];

  if (!changes.length) {
    const details = text(payload.TeamChanges);
    return details
      ? $localize`:@@notificationBodyTeamLegacy:In ${project}:project:: ${details}:details:`
      : $localize`:@@notificationBodyTeamEdited:The team of ${project}:project: was changed.`;
  }

  const [first] = changes;
  const role = text(first.Role);
  const newPerson = text(first.Current);
  const oldPerson = text(first.Previous);
  const more = moreChanges(changes.length - 1);

  if (newPerson && oldPerson) {
    return (
      $localize`:@@notificationBodyTeamHandover:In ${project}:project:, ${newPerson}:newPerson: takes over ${role}:role: from ${oldPerson}:oldPerson:.` +
      more
    );
  }

  if (newPerson) {
    return (
      $localize`:@@notificationBodyTeamJoined:In ${project}:project:, ${newPerson}:newPerson: takes over ${role}:role:.` +
      more
    );
  }

  return (
    $localize`:@@notificationBodyTeamLeft:In ${project}:project:, ${oldPerson}:oldPerson: is no longer responsible for ${role}:role:.` +
    more
  );
}

// #4–6
function roleChanged(key: string, payload: Payload): string {
  const project = text(payload.ProjectName);
  const role = text(payload.Role);
  const oldRole = text(payload.PreviousRole);

  switch (key) {
    case 'RoleAssigned':
      return role
        ? $localize`:@@notificationBodyRoleAssigned:You take over ${role}:role: in ${project}:project:.`
        : $localize`:@@notificationBodyRoleAssignedLegacy:You have a new role in ${project}:project:.`;
    case 'RoleRemoved':
      return oldRole
        ? $localize`:@@notificationBodyRoleRemoved:In ${project}:project:, you are no longer responsible for ${oldRole}:oldRole:.`
        : $localize`:@@notificationBodyRoleRemovedLegacy:You no longer have a role in ${project}:project:.`;
    default:
      return role && oldRole
        ? $localize`:@@notificationBodyRoleChanged:In ${project}:project:, you now take over ${role}:role: instead of ${oldRole}:oldRole:.`
        : $localize`:@@notificationBodyRoleChangedLegacy:Your role in ${project}:project: has changed.`;
  }
}

function statusWord(payload: Payload): string {
  const token = text(payload.Status).toUpperCase();
  return token ? (STATUS_WORDS[token] ?? text(payload.Status)) : '';
}

function priorityWord(payload: Payload): string {
  const token = text(payload.Priority).toUpperCase();
  return token ? (PRIORITY_WORDS[token] ?? text(payload.Priority)) : '';
}

// A site task is shared by every project on the site, so it is placed by the site's name instead.
function taskContext(payload: Payload): string {
  return text(payload.ProjectName) || text(payload.SiteName);
}

// #7a–7c
function taskChangedTitle(actor: string, payload: Payload): string {
  const status = statusWord(payload);
  const priority = priorityWord(payload);

  if (status && !priority)
    return $localize`:@@notificationTitleTaskStatusChanged:${actor}:actor: changed the status of a task`;
  if (priority && !status)
    return $localize`:@@notificationTitleTaskPriorityChanged:${actor}:actor: changed the priority of a task`;

  return $localize`:@@notificationTitleTaskChanged:${actor}:actor: edited a task`;
}

function taskChanged(payload: Payload): string {
  const task = text(payload.TaskTitle);
  const project = taskContext(payload);
  const status = statusWord(payload);
  const priority = priorityWord(payload);

  if (status && priority) {
    return $localize`:@@notificationBodyTaskStatusAndPriority:“${task}:task:” in ${project}:project: is now ${status}:status: and has ${priority}:priority: priority.`;
  }
  if (status)
    return $localize`:@@notificationBodyTaskStatus:“${task}:task:” in ${project}:project: is now ${status}:status:.`;
  if (priority)
    return $localize`:@@notificationBodyTaskPriority:“${task}:task:” in ${project}:project: now has ${priority}:priority: priority.`;

  return $localize`:@@notificationBodyTaskEdited:“${task}:task:” in ${project}:project: was edited.`;
}

const REVIEWER_LACKS_VALIDATION_NOTE = $localize`:@@notificationReviewerLacksValidation:You currently have no review rights for this task. Please ask your administrator to grant them.`;

// #8–14
function taskEvent(key: string, payload: Payload): string {
  const task = text(payload.TaskTitle);
  const project = taskContext(payload);

  switch (key) {
    case 'TaskAssigned':
      return $localize`:@@notificationBodyTaskAssigned:You are now responsible for “${task}:task:” in ${project}:project:.`;
    case 'TaskUnassigned':
      return $localize`:@@notificationBodyTaskUnassigned:You are no longer responsible for “${task}:task:” in ${project}:project:.`;
    case 'TaskCompleted':
      return $localize`:@@notificationBodyTaskCompleted:“${task}:task:” in ${project}:project: is completed.`;
    case 'TaskNotRequired':
      return $localize`:@@notificationBodyTaskNotRequired:“${task}:task:” in ${project}:project: is no longer needed.`;
    case 'TaskUnderReview': {
      const body = $localize`:@@notificationBodyTaskUnderReview:“${task}:task:” in ${project}:project: is done and waiting for your approval.`;
      // A reviewer reached as a fallback up the reporting chain lacks validation rights (#4631).
      return payload.ReviewerLacksValidation === true ? `${body} ${REVIEWER_LACKS_VALIDATION_NOTE}` : body;
    }
    case 'TaskRejected':
      return $localize`:@@notificationBodyTaskRejected:“${task}:task:” in ${project}:project: was rejected and needs rework.`;
    case 'TaskReminder': {
      // #4967: sent by a rule of the task template. Says why (the rule's condition, worded by the
      // API), by when, and for a manager or department whose task it is - then the admin's comment.
      const reason = text(payload.Reason);
      const due = toDate(payload.DueDate);
      let body = reason
        ? $localize`:@@notificationBodyTaskReminderReason:“${task}:task:” in ${project}:project:: ${reason}:reason:`
        : due
          ? $localize`:@@notificationBodyTaskReminderDue:“${task}:task:” in ${project}:project: is due on ${formatDate(due)}:date:.`
          : $localize`:@@notificationBodyTaskReminder:Reminder for “${task}:task:” in ${project}:project:.`;
      const assignedTo = text(payload.AssignedTo);
      if (assignedTo) body += ' ' + $localize`:@@notificationBodyTaskReminderAssignedTo:Assigned to ${assignedTo}:name:.`;
      const comment = text(payload.Comment);
      return comment ? `${body} ${quoted(truncate(comment))}` : body;
    }
    default: {
      const quote = quoted(truncate(text(payload.Comment)));
      return $localize`:@@notificationBodyTaskComment:On “${task}:task:” in ${project}:project:: ${quote}:quote:`;
    }
  }
}

// #15–17
function sparkEvent(key: string, payload: Payload): string {
  const project = text(payload.ProjectName);

  const quote =
    key === 'SparkReacted' || key === 'SparkLiked'
      ? quoted(truncate(text(payload.Spark), MAX_SPARK_EXCERPT_LENGTH))
      : quoted(truncate(text(key === 'SparkAnswered' ? payload.Answer : payload.Spark)));

  return $localize`:@@notificationBodySpark:In ${project}:project:: ${quote}:quote:`;
}

// #18
function contractChanged(payload: Payload): string {
  const contract = text(payload.Designation);
  const changes = changedFields(payload, CONTRACT_FIELDS);

  const runtimeChanged = typeof payload.IndefiniteRuntime === 'boolean';
  const linked = text(payload.ProjectsAdded);
  const unlinked = text(payload.ProjectsRemoved);
  const documentAdded = text(payload.DocumentAdded);
  const documentRemoved = text(payload.DocumentRemoved);

  const sentences: string[] = [
    ...changes.map(({ field, value }) =>
      value
        ? $localize`:@@notificationBodyContractField:For “${contract}:contract:”, ${field}:field: is now ${truncate(value, MAX_SPARK_EXCERPT_LENGTH)}:value:.`
        : $localize`:@@notificationBodyContractFieldCleared:For “${contract}:contract:”, ${field}:field: was removed.`
    ),
    ...(runtimeChanged
      ? [
          payload.IndefiniteRuntime
            ? $localize`:@@notificationBodyContractIndefinite:“${contract}:contract:” now runs indefinitely.`
            : $localize`:@@notificationBodyContractLimited:“${contract}:contract:” now has a fixed term.`,
        ]
      : []),
    ...(linked
      ? [$localize`:@@notificationBodyContractLinked:“${contract}:contract:” is now linked to ${linked}:projects:.`]
      : []),
    ...(unlinked
      ? [
          $localize`:@@notificationBodyContractUnlinked:“${contract}:contract:” is no longer linked to ${unlinked}:projects:.`,
        ]
      : []),
    ...(documentAdded
      ? [
          $localize`:@@notificationBodyContractDocumentAdded:For “${contract}:contract:”, the document “${documentAdded}:document:” was added.`,
        ]
      : []),
    ...(documentRemoved
      ? [
          $localize`:@@notificationBodyContractDocumentRemoved:For “${contract}:contract:”, the document “${documentRemoved}:document:” was removed.`,
        ]
      : []),
  ];

  if (!sentences.length) return $localize`:@@notificationBodyContractEdited:“${contract}:contract:” was edited.`;

  return sentences[0] + moreChanges(sentences.length - 1);
}

// Counted from the day the reminder went out, not from today: the row keeps saying what it said.
function deadlineDays(payload: Payload, occurredAt?: string): number | null {
  const due = toDate(payload.DeadlineDate);

  if (!due) return null;

  const sent = occurredAt ? new Date(occurredAt) : new Date();
  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  return Math.max(0, Math.round((startOfDay(due) - startOfDay(sent)) / DAY_MS));
}

// #19a–19b
function deadlineTitle(payload: Payload, occurredAt?: string): string {
  const days = deadlineDays(payload, occurredAt);

  if (days === null) return $localize`:@@notificationTitleDeadlineApproaching:Deadline approaching`;
  if (days === 0) return $localize`:@@notificationTitleDeadlineToday:Deadline ends today`;
  if (days === 1) return $localize`:@@notificationTitleDeadlineTomorrow:Deadline ends tomorrow`;

  return $localize`:@@notificationTitleDeadlineDays:Deadline ends in ${days}:days: days`;
}

// #19–20
function deadline(key: string, payload: Payload, occurredAt?: string): string {
  const contract = text(payload.Designation);
  const due = toDate(payload.DeadlineDate);

  if (key === 'DeadlineApproaching' && deadlineDays(payload, occurredAt) === 0) {
    return $localize`:@@notificationBodyDeadlineToday:The deadline for “${contract}:contract:” ends today.`;
  }

  if (!due) return $localize`:@@notificationBodyDeadlineUndated:There is a deadline for “${contract}:contract:”.`;

  return $localize`:@@notificationBodyDeadline:The deadline for “${contract}:contract:” ends on ${formatDate(due)}:date:.`;
}

// The API sends money pre-formatted and signed ("+ 1.234,56 €"); a sentence says rise or fall
// instead of the sign.
function unsigned(value: string): string {
  return value.replace(/^[+\-−]\s*/, '');
}

function costIncreased(payload: Payload): boolean {
  if (typeof payload.PlannedNettIncreased === 'boolean') return payload.PlannedNettIncreased;

  return !/^[\-−]/.test(text(payload.PlannedNettDelta));
}

// #21
function costChanged(payload: Payload): string {
  const project = text(payload.ProjectName);
  const amount = text(payload.PlannedNett);
  const difference = unsigned(text(payload.PlannedNettDelta));

  if (!difference)
    return $localize`:@@notificationBodyCostNow:In project ${project}:project:, planned costs are now ${amount}:amount: net.`;

  return costIncreased(payload)
    ? $localize`:@@notificationBodyCostRise:In project ${project}:project:, planned costs rise by ${difference}:difference: to ${amount}:amount: net.`
    : $localize`:@@notificationBodyCostFall:In project ${project}:project:, planned costs fall by ${difference}:difference: to ${amount}:amount: net.`;
}

// #22
function costThresholdTitle(payload: Payload): string {
  const change = (text(payload.PlannedNettDeltaPercent) || text(payload.PlannedNettDelta)).replace(
    /^([+\-−])\s+/,
    '$1'
  );

  return $localize`:@@notificationTitleCostThresholdExceeded:Large cost change: ${change}:percent:`;
}

function costThreshold(payload: Payload): string {
  const project = text(payload.ProjectName);
  const amount = text(payload.PlannedNett);
  const by = unsigned(text(payload.PlannedNettDeltaPercent) || text(payload.PlannedNettDelta));

  if (project)
    return costIncreased(payload)
      ? $localize`:@@notificationBodyCostThresholdRiseInProject:In project ${project}:project:, planned costs rise by ${by}:percent: to ${amount}:amount: net. This exceeds your threshold.`
      : $localize`:@@notificationBodyCostThresholdFallInProject:In project ${project}:project:, planned costs fall by ${by}:percent: to ${amount}:amount: net. This exceeds your threshold.`;

  return costIncreased(payload)
    ? $localize`:@@notificationBodyCostThresholdRise:Planned costs rise by ${by}:percent: to ${amount}:amount: net. This exceeds your threshold.`
    : $localize`:@@notificationBodyCostThresholdFall:Planned costs fall by ${by}:percent: to ${amount}:amount: net. This exceeds your threshold.`;
}

function costDetailsChanged(payload: Payload): string {
  const cost = text(payload.Cost);
  const project = text(payload.ProjectName);
  const changes = changedFields(payload, COST_FIELDS);

  const sentences = [
    ...changes.map(({ field, value }) =>
      value
        ? $localize`:@@notificationBodyCostField:For “${cost}:cost:” in ${project}:project:, ${field}:field: is now ${value}:value:.`
        : $localize`:@@notificationBodyCostFieldCleared:For “${cost}:cost:” in ${project}:project:, ${field}:field: was removed.`
    ),
    ...(typeof payload.Commissioned === 'boolean'
      ? [
          payload.Commissioned
            ? $localize`:@@notificationBodyCostCommissioned:“${cost}:cost:” in ${project}:project: is now commissioned.`
            : $localize`:@@notificationBodyCostNotCommissioned:“${cost}:cost:” in ${project}:project: is no longer commissioned.`,
        ]
      : []),
  ];

  if (!sentences.length)
    return $localize`:@@notificationBodyCostEdited:“${cost}:cost:” in ${project}:project: was edited.`;

  return sentences[0] + moreChanges(sentences.length - 1);
}

function isoWeek(date: Date): number {
  const day = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const weekday = day.getUTCDay() || 7;
  day.setUTCDate(day.getUTCDate() + 4 - weekday);
  const yearStart = new Date(Date.UTC(day.getUTCFullYear(), 0, 1));

  return Math.ceil(((day.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7);
}

function reportPeriod(payload: Payload): string {
  const created = payload.CreatedOn ? new Date(payload.CreatedOn) : null;

  if (!created || isNaN(created.getTime())) return text(payload.Window);

  if (payload.Period === 'MONTHLY') {
    return created.toLocaleDateString($localize.locale || 'en', { month: 'long', year: 'numeric' });
  }

  const week = isoWeek(created);

  return $localize`:@@notificationReportWeek:CW ${week}:week:`;
}

function list(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';

  const last = parts[parts.length - 1];
  const rest = parts.slice(0, -1).join(', ');

  return $localize`:@@notificationListAnd:${rest}:rest: and ${last}:last:`;
}

// #23–24
// The figures are in the mail; the bell names the weeks it covers.
function costOverview(payload: Payload): string {
  const first = text(payload.FirstWeek);
  const last = text(payload.LastWeek);
  return first && last ? $localize`:@@notificationBodyCostOverview:Calendar weeks ${first}:first: to ${last}:last:` : '';
}

function report(payload: Payload): string {
  const period = reportPeriod(payload);
  const sections: any[] = Array.isArray(payload.Sections) ? payload.Sections : [];

  const filled = sections
    .map(section => ({ section, count: Array.isArray(section?.Rows) ? section.Rows.length : 0 }))
    .filter(({ count }) => count > 0)
    .map(({ section, count }) => REPORT_SECTIONS[section?.Key]?.(count) ?? `${text(section?.Heading)} (${count})`);

  if (!filled.length) return $localize`:@@notificationBodyReportEmpty:Nothing new for you in ${period}:period:.`;

  const contents = list(filled);

  return $localize`:@@notificationBodyReport:Your report for ${period}:period: contains ${contents}:sections:.`;
}

function description(source: NotificationTextSource, payload: Payload): string {
  const key = source.titleKey;

  switch (key) {
    case 'ProjectChanged':
      return projectChanged(payload);
    case 'ProjectDatesChanged':
      return scheduleMoved(payload);
    case 'ProjectTeamChanged':
      return teamChanged(payload);
    case 'RoleAssigned':
    case 'RoleChanged':
    case 'RoleRemoved':
      return roleChanged(key, payload);
    case 'TaskChanged':
      return taskChanged(payload);
    case 'TaskAssigned':
    case 'TaskUnassigned':
    case 'TaskCompleted':
    case 'TaskNotRequired':
    case 'TaskUnderReview':
    case 'TaskRejected':
    case 'TaskReminder':
    case 'TaskMentioned':
    case 'TaskCommented':
      return taskEvent(key, payload);
    case 'SparkMentioned':
    case 'SparkAnswered':
    case 'SparkReacted':
    case 'SparkLiked':
      return sparkEvent(key, payload);
    case 'ContractChanged':
      return contractChanged(payload);
    case 'DeadlineApproaching':
    case 'DeadlineAssigned':
      return deadline(key, payload, source.occurredAt);
    case 'CostChanged':
      return costChanged(payload);
    case 'CostThresholdExceeded':
      return costThreshold(payload);
    case 'CostDetailsChanged':
      return costDetailsChanged(payload);
    case 'WeeklySummary':
    case 'MonthlySummary':
      return report(payload);
    case 'CostOverviewReady':
      return costOverview(payload);
    case 'DatabaseExportReady':
      return $localize`:@@notificationBodyExportReady:You can now download the file.`;
    case 'DatabaseExportFailed':
      return $localize`:@@notificationBodyExportFailed:Please start the export again.`;
    default:
      return '';
  }
}

export function notificationText(source: NotificationTextSource): NotificationText {
  const payload = parse(source.payloadJson);

  return { title: title(source, payload), description: description(source, payload) };
}
